import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import ButtonComp from '@components/UI/Button/Index.jsx';
import InputTextModalComp from '@components/UI/InputTextModal/Index.jsx';
import SelectOptionModalComp from '@components/UI/SelectOptionModal/Index.jsx';
import { UpdateUsuario } from '@services/Usuario/Index.jsx';
import { flgStatusOptions } from '@enums/FlgStatus/Index.jsx';
import { perfilUsuarioOptions } from '@enums/Usuario/Index.jsx';
import { perfisVisitante } from '@utils/auth/perfis';
import ValidationsUsuario from '@validations/Usuario/Validations';
import { useFetchDataUnidades } from '@hooks/api/Unidade/useFetchDataUnidades';
import { useFetchSolicitacoesByUnidade } from '@hooks/api/Solicitacao/useFetchSolicitacoesByUnidade';

const toOption = (id, label) => (id ? { value: id, label: label ?? String(id) } : null);

function EditUsuarioModal({ onUpdate, visible, closeModal, selectedRow }) {
  const { unitOptions, unitLoading, fetchUnits } = useFetchDataUnidades();
  const { solicitacaoOptions, solicitacaoLoading, fetchSolicitacoes } = useFetchSolicitacoesByUnidade();

  const isVisitor = selectedRow && perfisVisitante.includes(selectedRow.perfil);
  const [codigo, setCodigo] = useState('');

  const [tempValues, setTempValues] = useState({
    nome: '',
    email: '',
    perfil: null,
    flgAtivo: null,
    unidade: null,
    solicitacao: null
  });

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  useEffect(() => {
    if (!isVisitor) return;
    const unidadeSel = tempValues.unidade;

    setTempValues(prev => ({ ...prev, solicitacao: null }));

    if (unidadeSel?.value) {
      fetchSolicitacoes(unidadeSel.value);
    }
  }, [fetchSolicitacoes, isVisitor, setTempValues]);

  useEffect(() => {
    if (selectedRow) {

      setCodigo(selectedRow.codigo);

      setTempValues({
        nome: selectedRow.nome,
        email: selectedRow.email,
        perfil: selectedRow.perfil,
        flgAtivo: selectedRow.flgAtivo,
        unidade: toOption(selectedRow.idUnidadeGerencia, selectedRow.nomeUnidadeGerencia),
        solicitacao: toOption(selectedRow.idSolicitacao, selectedRow.numeroSolicitacao),
      });
    }

    // Se já tiver unidade, traz solicitações e seleciona a atual
    if (selectedRow?.idUnidadeGerencia) {
      fetchSolicitacoes(selectedRow.idUnidadeGerencia);
    }
  }, [selectedRow]);

  const checkIsDirty = () => {
    if (!selectedRow) return false;
    if (
      selectedRow.nome !== tempValues.nome ||
      selectedRow.email !== tempValues.email
    ) {
      return true;
    }
    if (isVisitor) {
      if (
        selectedRow.idUnidadeGerencia !== tempValues.unidade?.value ||
        selectedRow.idSolicitacao !== tempValues.solicitacao?.value
      ) {
        return true;
      }
    }
    return false;
  };

  const handleTempChange = (field, value) => {
    if (field === 'unidade') {
      setTempValues(prev => ({
        ...prev,
        unidade: value || null, // garante null, não undefined
        solicitacao: null
      }));

      if (isVisitor && value?.value) {
        fetchSolicitacoes(value.value);
      }
      return;
    }

    setTempValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSubmit = async () => {
    try {
      if (!checkIsDirty()) {
        SweetAlert.error('Erro', 'Nenhuma alteração foi feita nos dados.');
        return;
      }

      // Validação de e-mail
      const { validateEmail } = ValidationsUsuario();
      const emailErrors = validateEmail({ email: tempValues.email });
      if (Object.keys(emailErrors).length > 0) {
        SweetAlert.error(
          'Erro na Validação',
          Object.values(emailErrors).join('\n')
        );
        return;
      }

      // Se for visitante, exige unidade e solicitação
      if (isVisitor) {
        if (!tempValues.unidade || !tempValues.solicitacao) {
          SweetAlert.error(
            'Erro',
            'Unidade e Solicitação são obrigatórios para visitantes.'
          );
          return;
        }
      }

      // Constroi payload
      const payload = {
        nome: tempValues.nome,
        email: tempValues.email,
        ...(isVisitor && {
          idUnidadeGerencia: tempValues.unidade.value,
          idSolicitacao: tempValues.solicitacao.value
        })
      };

      SweetAlert.loading();
      const result = await UpdateUsuario(selectedRow.id, payload);

      onUpdate?.(result);

      closeModal();

      SweetAlert.success(
        'Atualização Concluída',
        'Os dados foram atualizados com sucesso.'
      );
    } catch (error) {
      SweetAlert.error(
        'Erro ao atualizar',
        error.parsed?.message || 'Não foi possível atualizar o usuário.'
      );
      console.error(error);
    }
  };

  const handleCloseWithReset = () => {
    setTempValues({
      nome: selectedRow.nome,
      email: selectedRow.email,
      perfil: selectedRow.perfil,
      flgAtivo: selectedRow.flgAtivo,
      unidade: toOption(selectedRow.idUnidadeGerencia, selectedRow.nomeUnidadeGerencia),
      solicitacao: toOption(selectedRow.idSolicitacao, selectedRow.numeroSolicitacao),
    });
    closeModal();
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      footer={null}
      width={800}
      title={<h5 className="fw-bold">Edição de Usuário</h5>}
    >
      <hr />

      <InputTextModalComp
        label="Código"
        value={codigo}
        placeholder="Código"
        disabled
      />

      <InputTextModalComp
        label="Nome"
        value={tempValues.nome}
        onChange={e => handleTempChange('nome', e.target.value)}
        required
        maxLength={255}
      />

      <InputTextModalComp
        label="E-mail"
        value={tempValues.email}
        onChange={e => handleTempChange('email', e.target.value)}
        required
        maxLength={255}
      />

      {/* Perfil e Status somente leitura */}
      <SelectOptionModalComp
        label="Perfil"
        value={perfilUsuarioOptions.find(option => option.value === tempValues.perfil)}
        isDisabled
      />

      <SelectOptionModalComp
        label="Status"
        value={flgStatusOptions.find(option => option.value === tempValues.flgAtivo)}
        isDisabled
      />

      {/* Unidade e Solicitação: editáveis apenas para visitante */}
      {(isVisitor) && (
        <>
          <SelectOptionModalComp
            label="Unidade"
            options={unitOptions}
            value={tempValues.unidade}
            onChange={opt => handleTempChange('unidade', opt)}
            loading={unitLoading}
            isDisabled={!isVisitor}
            required={isVisitor}
            placeholder={'Selecione uma unidade/gerência'}
          />

          <SelectOptionModalComp
            label="Número Solicitação"
            options={solicitacaoOptions}
            value={tempValues.solicitacao}
            onChange={opt => handleTempChange('solicitacao', opt)}
            loading={solicitacaoLoading}
            isDisabled={!isVisitor}
            required={isVisitor}
            placeholder={'Selecione uma solicitação'}
          />

        </>
      )}

      {/* Botões */}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          title="ATUALIZAR"
          buttonColor="#2b97be"
          classnameIcon="fa-regular fa-floppy-disk"
          onClick={onSubmit}
          hoverMessage="Confirma as alterações"
        />
      </div>
    </Modal>
  );
}

export default EditUsuarioModal;
